
import React, { useState } from 'react';
import { 
  Search, Plus, Mail, Phone, Building2, X, Edit2, Trash2,
  StickyNote, ChevronLeft, Clock, Save, Loader2, ChevronRight,
  MessageSquareText, Eye, User, Briefcase, Settings2, Palette, Check,
  MapPin, Home, Building, ArrowUpRight
} from 'lucide-react';
import { Contact, ContactClassification, Location, Inspection, Note, NoteCategory } from '../types';
import { supabase, toSnakeCase, getErrorMessage } from '../lib/supabase';

interface ContactsProps {
  contacts: Contact[];
  setContacts: React.Dispatch<React.SetStateAction<Contact[]>>;
  locations: Location[];
  inspections: Inspection[];
  notes: Note[];
  setNotes: React.Dispatch<React.SetStateAction<Note[]>>;
  categories: NoteCategory[];
  classifications: ContactClassification[];
  setClassifications: React.Dispatch<React.SetStateAction<ContactClassification[]>>;
  onNavigateToLocation?: (locationId: string) => void;
}

const Contacts: React.FC<ContactsProps> = ({ 
  contacts, setContacts, locations, inspections, notes, setNotes, categories, classifications, setClassifications, onNavigateToLocation
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isTypeRegistryOpen, setIsTypeRegistryOpen] = useState(false);
  const [editingContactId, setEditingContactId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  
  // Type Registry state
  const [editingTypeId, setEditingTypeId] = useState<string | null>(null);
  const [typeFormData, setTypeFormData] = useState<Partial<ContactClassification>>({ name: '', color: '#3b82f6' });

  // Note local states
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [newNote, setNewNote] = useState({ title: '', content: '', categoryId: categories[0]?.id || '' });
  const [viewingNote, setViewingNote] = useState<Note | null>(null);

  const [formData, setFormData] = useState<Partial<Contact>>({
    name: '', email: '', phone: '', typeId: classifications[0]?.id || '', company: ''
  });

  const filteredContacts = contacts.filter(c => 
    (c.name || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
    (c.company || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.email || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedContact = contacts.find(c => c.id === selectedContactId);
  const contactNotes = notes.filter(n => n.entityType === 'Contact' && n.entityId === selectedContactId);
  
  // Derived state: find locations where this contact is associated
  const linkedLocations = locations.filter(loc => loc.contactIds?.includes(selectedContactId || ''));
  
  const getClassification = (id: string) => classifications.find(cl => cl.id === id);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email) return;
    setSaving(true);
    try {
      const payload = toSnakeCase(formData);
      if (editingContactId) {
        await supabase.from('contacts').update(payload).eq('id', editingContactId);
        setContacts(prev => prev.map(c => c.id === editingContactId ? { ...c, ...formData } as Contact : c));
      } else {
        const { data, error } = await supabase.from('contacts').insert([payload]).select().single();
        if (error) throw error;
        setContacts(prev => [...prev, { ...formData, id: data.id, createdAt: data.created_at } as Contact]);
      }
      setIsSidebarOpen(false);
    } catch (err) { alert(`Error: ${getErrorMessage(err)}`); }
    finally { setSaving(false); }
  };

  const handleDeleteContact = async (id: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!id) return;
    
    const confirmMessage = "Permanently purge this stakeholder from the registry? All associated interaction logs will be lost.";
    if (!window.confirm(confirmMessage)) return;
    
    setSaving(true);
    try {
      const { error } = await supabase.from('contacts').delete().eq('id', id);
      if (error) throw error;
      
      setContacts(prev => prev.filter(c => c.id !== id));
      // Fix: Use correct state setter setSelectedContactId instead of undefined setSelectedLocationId
      if (selectedContactId === id) setSelectedContactId(null);
      if (editingContactId === id) setEditingContactId(null);
      setIsSidebarOpen(false);
    } catch (err) {
      alert(`Deletion failed: ${getErrorMessage(err)}`);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveType = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!typeFormData.name) return;
    setSaving(true);
    try {
      const payload = toSnakeCase(typeFormData);
      if (editingTypeId) {
        const { error } = await supabase.from('contact_classifications').update(payload).eq('id', editingTypeId);
        if (error) throw error;
        setClassifications(prev => prev.map(c => c.id === editingTypeId ? { ...c, ...typeFormData } as ContactClassification : c));
      } else {
        const { data, error } = await supabase.from('contact_classifications').insert([payload]).select().single();
        if (error) throw error;
        setClassifications(prev => [...prev, { ...typeFormData, id: data.id } as ContactClassification]);
      }
      setTypeFormData({ name: '', color: '#3b82f6' });
      setEditingTypeId(null);
    } catch (err) { alert(`Error saving type: ${getErrorMessage(err)}`); }
    finally { setSaving(false); }
  };

  const handleDeleteType = async (id: string) => {
    if (contacts.some(c => c.typeId === id)) {
      alert("Cannot delete classification while contacts are still assigned to it.");
      return;
    }
    if (!confirm('Permanently remove this classification?')) return;
    try {
      const { error } = await supabase.from('contact_classifications').delete().eq('id', id);
      if (error) throw error;
      setClassifications(prev => prev.filter(c => c.id !== id));
    } catch (err) { alert(`Error: ${getErrorMessage(err)}`); }
  };

  const handleAddContactNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newNote.title || !newNote.content || !selectedContactId) return;
    setSaving(true);
    try {
      const payload = {
        title: newNote.title,
        content: newNote.content,
        entity_type: 'Contact',
        entity_id: selectedContactId,
        category_id: newNote.categoryId
      };
      const { data, error } = await supabase.from('notes').insert([payload]).select().single();
      if (error) throw error;
      setNotes(prev => [{
        id: data.id,
        title: data.title,
        content: data.content,
        createdAt: data.created_at,
        entityType: 'Contact',
        entityId: selectedContactId,
        categoryId: data.category_id
      }, ...prev]);
      setIsAddingNote(false);
      setNewNote({ title: '', content: '', categoryId: categories[0]?.id || '' });
    } catch (err) { alert(`Note creation failed: ${getErrorMessage(err)}`); }
    finally { setSaving(false); }
  };

  return (
    <div className="animate-in fade-in duration-300">
      {selectedContactId && selectedContact ? (
        <div className="space-y-6">
          <div className="flex items-center justify-between bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <div className="flex items-center gap-4">
              <button onClick={() => setSelectedContactId(null)} className="p-2 hover:bg-slate-100 rounded-lg transition-colors text-slate-400">
                <ChevronLeft size={20} />
              </button>
              <div>
                <h3 className="text-lg font-semibold text-slate-800 leading-none">{selectedContact.name}</h3>
                <p className="text-xs text-slate-500 mt-1 font-medium">{selectedContact.company || 'Private Participant'}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button 
                onClick={() => { setEditingContactId(selectedContact.id); setFormData({...selectedContact}); setIsSidebarOpen(true); }}
                className="px-4 py-2 text-xs font-semibold bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-all flex items-center gap-2 shadow-sm"
              >
                <Edit2 size={14} /> Edit Record
              </button>
              <button 
                onClick={(e) => handleDeleteContact(selectedContact.id, e)}
                className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-all shadow-sm"
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-5">
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-slate-600">
                    <div className="p-2 bg-slate-50 rounded-lg text-slate-400"><Mail size={14} /></div>
                    <span className="text-sm font-medium">{selectedContact.email}</span>
                  </div>
                  <div className="flex items-center gap-3 text-slate-600">
                    <div className="p-2 bg-slate-50 rounded-lg text-slate-400"><Phone size={14} /></div>
                    <span className="text-sm font-medium">{selectedContact.phone}</span>
                  </div>
                  <div className="flex items-center gap-3 text-slate-600">
                    <div className="p-2 bg-slate-50 rounded-lg text-slate-400"><Briefcase size={14} /></div>
                    <span className="text-sm font-medium">{selectedContact.company || 'Not Specified'}</span>
                  </div>
                </div>
                <div className="pt-4 border-t border-slate-100">
                  <span 
                    className="text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-md"
                    style={{ 
                      backgroundColor: `${getClassification(selectedContact.typeId)?.color}15`, 
                      color: getClassification(selectedContact.typeId)?.color 
                    }}
                  >
                    {getClassification(selectedContact.typeId)?.name}
                  </span>
                </div>
              </div>

              {/* Linked Properties Section */}
              <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
                <div className="p-4 bg-slate-50 border-b border-slate-100 flex items-center gap-2">
                  <MapPin size={16} className="text-blue-500" />
                  <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Linked Assets</h4>
                </div>
                <div className="divide-y divide-slate-100">
                  {linkedLocations.length > 0 ? linkedLocations.map(loc => (
                    <button 
                      key={loc.id} 
                      onClick={() => onNavigateToLocation?.(loc.id)}
                      className="w-full text-left p-4 flex items-center gap-3 group hover:bg-slate-50 transition-colors"
                    >
                      <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400 shrink-0 group-hover:bg-blue-50 group-hover:text-blue-600 transition-all overflow-hidden">
                        {loc.image ? <img src={loc.image} className="w-full h-full object-cover" /> : <Home size={18} />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-bold text-slate-800 truncate leading-none mb-1.5">{loc.name || loc.address}</p>
                        <p className="text-[10px] font-semibold text-slate-400 truncate flex items-center gap-1">
                          <MapPin size={10} /> {loc.city}, {loc.state}
                        </p>
                      </div>
                      <ArrowUpRight size={14} className="text-slate-200 group-hover:text-blue-500 transition-colors" />
                    </button>
                  )) : (
                    <div className="p-8 text-center"><p className="text-[10px] font-bold text-slate-300 uppercase tracking-widest">No Assets Linked</p></div>
                  )}
                </div>
              </section>
            </div>

            <div className="lg:col-span-2 space-y-6">
              <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
                {!viewingNote ? (
                  <div className="p-6">
                    <div className="flex justify-between items-center mb-6">
                      <div>
                        <h4 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                          <MessageSquareText size={16} className="text-blue-500" /> Interaction Log
                        </h4>
                        <p className="text-[10px] text-slate-400 font-medium uppercase tracking-wider mt-0.5">Contact-specific activity registry</p>
                      </div>
                      <button onClick={() => setIsAddingNote(!isAddingNote)} className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all flex items-center gap-2 ${isAddingNote ? 'bg-slate-100 text-slate-500' : 'bg-blue-600 text-white shadow-sm hover:bg-blue-700'}`}>
                        {isAddingNote ? <><X size={14} /> Cancel</> : <><Plus size={14} /> Add Log</>}
                      </button>
                    </div>

                    {isAddingNote && (
                      <form onSubmit={handleAddContactNote} className="mb-6 p-5 bg-slate-50 rounded-xl border border-slate-100 space-y-4 animate-in slide-in-from-top-2 duration-300">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block mb-1.5">Subject</label>
                            <input required className="w-full p-2.5 bg-white border border-slate-200 rounded-lg text-sm font-medium" value={newNote.title} onChange={(e) => setNewNote({...newNote, title: e.target.value})} placeholder="e.g. Rate negotiation" />
                          </div>
                          <div>
                            <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block mb-1.5">Classification</label>
                            <select className="w-full p-2.5 bg-white border border-slate-200 rounded-lg text-sm font-medium" value={newNote.categoryId} onChange={(e) => setNewNote({...newNote, categoryId: e.target.value})}>
                              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                          </div>
                        </div>
                        <div>
                          <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block mb-1.5">Observation Details</label>
                          <textarea required rows={3} className="w-full p-3 bg-white border border-slate-200 rounded-lg text-sm font-medium leading-relaxed" value={newNote.content} onChange={(e) => setNewNote({...newNote, content: e.target.value})} placeholder="Detailed notes about the interaction..." />
                        </div>
                        <div className="flex justify-end pt-2">
                          <button disabled={saving} type="submit" className="bg-slate-900 text-white px-6 py-2 rounded-lg text-xs font-semibold flex items-center gap-2 shadow-sm hover:bg-slate-800 transition-all">
                            {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Persist to Log
                          </button>
                        </div>
                      </form>
                    )}

                    <div className="divide-y divide-slate-100 border border-slate-100 rounded-xl overflow-hidden bg-white">
                      {contactNotes.length > 0 ? contactNotes.map(note => (
                        <div key={note.id} className="p-4 flex items-center justify-between hover:bg-slate-50 transition-colors group cursor-pointer" onClick={() => setViewingNote(note)}>
                          <div className="flex items-center gap-4 min-w-0 flex-1">
                            <div className="p-2 bg-slate-50 text-slate-400 rounded-lg group-hover:bg-blue-50 group-hover:text-blue-500 transition-colors shrink-0">
                              <StickyNote size={16} />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 mb-0.5">
                                <h5 className="font-semibold text-slate-800 text-sm truncate">{note.title}</h5>
                                <span className="text-[9px] font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded uppercase shrink-0 tracking-tighter">
                                  {categories.find(c => c.id === note.categoryId)?.name}
                                </span>
                              </div>
                              <p className="text-xs text-slate-500 font-medium truncate max-w-xl">{note.content}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 shrink-0 pl-4">
                            <span className="text-[10px] font-medium text-slate-400">{new Date(note.createdAt).toLocaleDateString()}</span>
                            <Eye size={14} className="text-slate-300 group-hover:text-blue-500 transition-colors" />
                          </div>
                        </div>
                      )) : (
                        <div className="py-12 text-center bg-slate-50/50 rounded-xl">
                          <StickyNote size={24} className="mx-auto text-slate-200 mb-2" />
                          <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">No logs recorded</p>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="animate-in fade-in slide-in-from-left-2 duration-300">
                    <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                      <div className="flex items-center gap-3">
                        <button onClick={() => setViewingNote(null)} className="p-1.5 hover:bg-white rounded-lg text-slate-500 border border-transparent hover:border-slate-200 transition-all flex items-center gap-1.5 font-semibold text-[10px] uppercase tracking-wider">
                          <ChevronLeft size={14} /> Back
                        </button>
                        <div className="h-4 w-px bg-slate-200 mx-1" />
                        <h4 className="font-semibold text-slate-800 text-sm">{viewingNote.title}</h4>
                      </div>
                      <button onClick={() => setViewingNote(null)} className="p-1.5 text-slate-400 hover:text-slate-600 transition-colors"><X size={18} /></button>
                    </div>
                    <div className="p-8 min-h-[300px]">
                      <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100 shadow-inner relative overflow-hidden">
                        <div className="absolute top-0 right-0 p-6 text-slate-200/50 -rotate-12 select-none"><MessageSquareText size={80} /></div>
                        <p className="text-slate-700 text-sm font-medium leading-relaxed whitespace-pre-wrap relative z-10">{viewingNote.content}</p>
                      </div>
                    </div>
                  </div>
                )}
              </section>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h3 className="text-xl font-semibold text-slate-800 tracking-tight">Network Registry</h3>
              <p className="text-sm text-slate-500 font-medium mt-0.5">Industry contact management database.</p>
            </div>
            <div className="flex items-center gap-3 w-full md:w-auto">
              <div className="relative flex-1 md:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <input 
                  type="text" 
                  placeholder="Search name or organization..." 
                  className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium focus:ring-1 focus:ring-blue-500 outline-none shadow-sm transition-all"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <button 
                onClick={() => setIsTypeRegistryOpen(true)}
                className="p-2 bg-white border border-slate-200 text-slate-500 hover:text-blue-600 rounded-lg transition-all shadow-sm"
                title="Manage Contact Types"
              >
                <Settings2 size={20} />
              </button>
              <button 
                onClick={() => { setEditingContactId(null); setFormData({name: '', email: '', phone: '', typeId: classifications[0]?.id || '', company: ''}); setIsSidebarOpen(true); }} 
                className="bg-slate-900 text-white px-5 py-2 rounded-lg text-sm font-semibold shadow-sm hover:bg-slate-800 transition-all flex items-center gap-2 whitespace-nowrap"
              >
                <Plus size={16} /> New Contact
              </button>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-slate-50/50 border-b border-slate-200">
                  <th className="px-6 py-4 text-[10px] font-semibold text-slate-500 uppercase tracking-widest">Stakeholder</th>
                  <th className="px-6 py-4 text-[10px] font-semibold text-slate-500 uppercase tracking-widest hidden lg:table-cell">Organization</th>
                  <th className="px-6 py-4 text-[10px] font-semibold text-slate-500 uppercase tracking-widest hidden md:table-cell">Classification</th>
                  <th className="px-6 py-4 text-[10px] font-semibold text-slate-500 uppercase tracking-widest">Contact Intel</th>
                  <th className="px-6 py-4 text-[10px] font-semibold text-slate-500 uppercase tracking-widest text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredContacts.length > 0 ? filteredContacts.map(contact => {
                  const type = getClassification(contact.typeId);
                  return (
                    <tr 
                      key={contact.id} 
                      onClick={() => setSelectedContactId(contact.id)}
                      className="hover:bg-slate-50/80 transition-colors cursor-pointer group"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400 group-hover:bg-blue-50 group-hover:text-blue-500 transition-colors">
                            <User size={14} />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-800 truncate max-w-[200px]">{contact.name}</p>
                            <p className="text-xs text-slate-400 font-medium truncate lg:hidden">{contact.company || 'Private'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 hidden lg:table-cell">
                        <div className="flex items-center gap-2 text-slate-600">
                          <Building2 size={12} className="text-slate-300" />
                          <span className="text-xs font-medium">{contact.company || '--'}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 hidden md:table-cell">
                        <span 
                          className="text-[10px] font-semibold px-2 py-0.5 rounded-md uppercase tracking-wider"
                          style={{ backgroundColor: `${type?.color}15`, color: type?.color }}
                        >
                          {type?.name}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-0.5">
                          <div className="flex items-center gap-2 text-slate-500">
                            <Mail size={12} className="text-slate-300" />
                            <span className="text-xs font-medium">{contact.email}</span>
                          </div>
                          {contact.phone && (
                            <div className="flex items-center gap-2 text-slate-500">
                              <Phone size={12} className="text-slate-300" />
                              <span className="text-xs font-medium">{contact.phone}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button 
                            onClick={(e) => handleDeleteContact(contact.id, e)} 
                            className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                            title="Delete Contact"
                          >
                            <Trash2 size={16} />
                          </button>
                          <div className="inline-flex items-center justify-center p-2 text-slate-400 group-hover:text-blue-600 group-hover:bg-blue-50 rounded-lg transition-all">
                            <ChevronRight size={16} />
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                }) : (
                  <tr>
                    <td colSpan={5} className="py-20 text-center text-slate-400">
                      <Search size={32} className="mx-auto mb-3 opacity-20" />
                      <p className="text-sm font-medium italic">No stakeholders indexed matching criteria.</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Contact Type Registry Modal */}
      {isTypeRegistryOpen && (
        <div className="fixed inset-0 z-[400] flex items-center justify-center p-6">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity" onClick={() => setIsTypeRegistryOpen(false)} />
          <div className="bg-white w-full max-w-xl rounded-3xl shadow-2xl relative overflow-hidden animate-in zoom-in-95 duration-300 flex flex-col max-h-[80vh]">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-600 text-white rounded-lg"><Palette size={20} /></div>
                <div>
                  <h3 className="font-semibold text-slate-800 text-sm uppercase tracking-widest">Type Taxonomy Registry</h3>
                  <p className="text-[10px] text-slate-400 font-medium uppercase tracking-wider">Classification Architecture</p>
                </div>
              </div>
              <button onClick={() => setIsTypeRegistryOpen(false)} className="p-2 hover:bg-white rounded-full text-slate-400 transition-colors"><X size={20} /></button>
            </div>
            
            <div className="p-8 overflow-y-auto space-y-8 no-scrollbar">
              <form onSubmit={handleSaveType} className="p-6 bg-slate-50 rounded-2xl border border-slate-100 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest block mb-1.5">Type Identity</label>
                    <input 
                      required 
                      className="w-full p-2.5 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-800 focus:ring-1 focus:ring-blue-500 outline-none" 
                      value={typeFormData.name} 
                      onChange={(e) => setTypeFormData({...typeFormData, name: e.target.value})} 
                      placeholder="e.g. Preferred Vendor"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest block mb-1.5">Brand Palette</label>
                    <div className="flex gap-2">
                      <input 
                        type="color" 
                        className="w-10 h-10 rounded-lg bg-transparent border-none cursor-pointer p-0" 
                        value={typeFormData.color} 
                        onChange={(e) => setTypeFormData({...typeFormData, color: e.target.value})} 
                      />
                      <input 
                        className="flex-1 p-2.5 bg-white border border-slate-200 rounded-xl text-xs font-mono uppercase text-slate-600" 
                        value={typeFormData.color} 
                        onChange={(e) => setTypeFormData({...typeFormData, color: e.target.value})} 
                      />
                    </div>
                  </div>
                </div>
                <div className="flex justify-end pt-2">
                  <button disabled={saving} type="submit" className="bg-slate-900 text-white px-6 py-2.5 rounded-xl text-xs font-semibold flex items-center gap-2 shadow-sm hover:bg-slate-800">
                    {saving ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} />} {editingTypeId ? 'Modify Type' : 'Register Type'}
                  </button>
                  {editingTypeId && (
                    <button 
                      type="button" 
                      onClick={() => { setEditingTypeId(null); setTypeFormData({name: '', color: '#3b82f6'}); }}
                      className="ml-2 px-4 py-2.5 text-xs font-semibold text-slate-500 hover:text-slate-700"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>

              <div className="space-y-3">
                <h4 className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest border-b border-slate-100 pb-2">Active Classifications</h4>
                <div className="grid grid-cols-1 gap-2">
                  {classifications.map(cl => (
                    <div key={cl.id} className="p-4 bg-white border border-slate-100 rounded-2xl flex items-center justify-between group hover:border-slate-200 transition-all">
                      <div className="flex items-center gap-4">
                        <div className="w-4 h-4 rounded-full" style={{ backgroundColor: cl.color }}></div>
                        <span className="text-sm font-medium text-slate-700">{cl.name}</span>
                      </div>
                      <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => { setEditingTypeId(cl.id); setTypeFormData(cl); }} className="p-2 text-slate-400 hover:text-blue-600 transition-colors"><Edit2 size={14} /></button>
                        <button onClick={() => handleDeleteType(cl.id)} className="p-2 text-slate-400 hover:text-red-600 transition-colors"><Trash2 size={14} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contact Sidebar */}
      <div className={`fixed inset-0 z-[300] ${isSidebarOpen ? 'visible' : 'invisible'}`}>
        <div className="absolute inset-0 bg-slate-900/40 transition-opacity duration-200" onClick={() => setIsSidebarOpen(false)} />
        <div className={`absolute inset-y-0 right-0 w-full max-w-md bg-white shadow-xl transition-transform duration-300 ${isSidebarOpen ? 'translate-x-0' : 'translate-x-full'} flex flex-col`}>
          <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
            <h3 className="font-semibold text-slate-800 text-xs uppercase tracking-widest">{editingContactId ? 'Edit Identity Profile' : 'Register New Stakeholder'}</h3>
            <button onClick={() => setIsSidebarOpen(false)} className="p-2 hover:bg-white rounded-lg text-slate-400 transition-all"><X size={20} /></button>
          </div>
          <form onSubmit={handleSave} className="flex-1 p-8 space-y-6 overflow-y-auto no-scrollbar">
            <div className="space-y-1.5">
              <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest">Legal Identity</label>
              <input required className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-800 focus:bg-white focus:ring-1 focus:ring-blue-500 outline-none transition-all" value={formData.name} onChange={(e)=>setFormData({...formData, name: e.target.value})} placeholder="Full name..." />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest">Email Record</label>
              <input required type="email" className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-800 focus:bg-white focus:ring-1 focus:ring-blue-500 outline-none transition-all" value={formData.email} onChange={(e)=>setFormData({...formData, email: e.target.value})} placeholder="email@address.com" />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest">Mobile Intel</label>
              <input className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-800 focus:bg-white focus:ring-1 focus:ring-blue-500 outline-none transition-all" value={formData.phone} onChange={(e)=>setFormData({...formData, phone: e.target.value})} placeholder="(000) 000-0000" />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest">Assigned Organization</label>
              <input className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-800 focus:bg-white focus:ring-1 focus:ring-blue-500 outline-none transition-all" value={formData.company} onChange={(e)=>setFormData({...formData, company: e.target.value})} placeholder="Company name..." />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest">Entity Classification</label>
              <select className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-800 focus:bg-white focus:ring-1 focus:ring-blue-500 outline-none transition-all" value={formData.typeId} onChange={(e)=>setFormData({...formData, typeId: e.target.value})}>
                {classifications.map(cl => <option key={cl.id} value={cl.id}>{cl.name}</option>)}
              </select>
            </div>
          </form>
          <div className="p-6 border-t border-slate-100 flex gap-3 shadow-inner">
            <button type="button" onClick={() => setIsSidebarOpen(false)} className="flex-1 py-3 text-xs font-semibold text-slate-500 hover:bg-slate-50 rounded-xl border border-slate-200 transition-all uppercase tracking-widest">Abort</button>
            {editingContactId && (
              <button 
                type="button" 
                onClick={(e) => handleDeleteContact(editingContactId, e)}
                className="px-4 py-3 text-xs font-bold text-red-500 hover:bg-red-50 rounded-xl border border-red-100 transition-all uppercase tracking-widest flex items-center justify-center gap-2"
              >
                <Trash2 size={16} /> Purge
              </button>
            )}
            <button disabled={saving} onClick={handleSave} className="flex-[2] py-3 bg-slate-900 text-white font-semibold text-xs rounded-xl hover:bg-slate-800 transition-all flex items-center justify-center gap-2 shadow-sm uppercase tracking-widest">
              {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Commit Intel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contacts;
